import { DEFAULT_PACKAGE_FEATURE_FLAGS, PackageFeatureFlags } from '../types'
import { apiService } from './api.service'

type Listener = (flags: PackageFeatureFlags) => void

const POLL_INTERVAL_MS = 30_000

function normalizeFlags(data: Partial<PackageFeatureFlags> | null | undefined): PackageFeatureFlags {
  if (!data || typeof data !== 'object') {
    return { ...DEFAULT_PACKAGE_FEATURE_FLAGS }
  }

  const features =
    data.features && typeof data.features === 'object' && !Array.isArray(data.features) ? (data.features as Record<string, unknown>) : {}

  const rawModules = (data as any)?.modules
  const modulesSource =
    rawModules && typeof rawModules === 'object' && !Array.isArray(rawModules)
      ? rawModules
      : (features as any).modules && typeof (features as any).modules === 'object' && !Array.isArray((features as any).modules)
        ? ((features as any).modules as Record<string, unknown>)
        : {}

  const modules: Record<string, boolean> = {}
  Object.entries(modulesSource as Record<string, unknown>).forEach(([key, value]) => {
    modules[String(key)] = typeof value === 'boolean' ? value : Boolean(value)
  })
  ;(features as Record<string, unknown>).modules = modules

  const packageId =
    typeof data.packageId === 'string' || data.packageId === null || data.packageId === undefined ? (data.packageId ?? null) : String(data.packageId)
  const packageName = typeof data.packageName === 'string' ? data.packageName : null
  const updatedAt = typeof data.updatedAt === 'string' ? data.updatedAt : null

  return {
    packageId,
    packageName,
    features,
    updatedAt,
  }
}

class FeatureFlagsService {
  private flags: PackageFeatureFlags = { ...DEFAULT_PACKAGE_FEATURE_FLAGS }
  private listeners: Set<Listener> = new Set()
  private pollTimer: number | null = null
  private inFlight: Promise<PackageFeatureFlags> | null = null
  private featuresSignature: string = JSON.stringify(DEFAULT_PACKAGE_FEATURE_FLAGS.features)

  get current(): PackageFeatureFlags {
    return this.flags
  }

  async fetchFlags(): Promise<PackageFeatureFlags> {
    if (this.inFlight) {
      return this.inFlight
    }

    this.inFlight = (async () => {
      try {
        const response = await apiService.get<PackageFeatureFlags>('feature-flags/')
        const normalized = normalizeFlags(response.data)
        this.updateFlags(normalized)
        return normalized
      } catch (error) {
        // Reset to default on 404/connection issues so UI has predictable state.
        this.updateFlags({ ...DEFAULT_PACKAGE_FEATURE_FLAGS })
        throw error
      } finally {
        this.inFlight = null
      }
    })()

    return this.inFlight
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener)
    listener(this.flags)
    this.ensurePolling()

    return () => {
      this.listeners.delete(listener)
      if (this.listeners.size === 0) {
        this.stopPolling()
      }
    }
  }

  startPolling(): void {
    if (this.pollTimer !== null || typeof window === 'undefined') {
      return
    }

    this.fetchFlags().catch(() => {
      // Swallow fetch errors here; callers can inspect state if needed.
    })

    this.pollTimer = window.setInterval(() => {
      this.fetchFlags().catch(() => undefined)
    }, POLL_INTERVAL_MS)
  }

  stopPolling(): void {
    if (this.pollTimer !== null && typeof window !== 'undefined') {
      window.clearInterval(this.pollTimer)
      this.pollTimer = null
    }
  }

  private ensurePolling(): void {
    if (this.pollTimer === null && this.listeners.size > 0) {
      this.startPolling()
    }
  }

  private updateFlags(next: PackageFeatureFlags): void {
    const previous = this.flags
    const nextSignature = JSON.stringify(next.features)
    const changed =
      previous.packageId !== next.packageId ||
      previous.packageName !== next.packageName ||
      previous.updatedAt !== next.updatedAt ||
      this.featuresSignature != nextSignature

    if (!changed) {
      return
    }

    this.flags = next
    this.featuresSignature = nextSignature
    this.listeners.forEach((listener) => listener(this.flags))
  }
}

export const featureFlagsService = new FeatureFlagsService()
